import { useState } from 'react';
import { Badge, Panel } from '@usacow/ds';

interface Concept {
  name: string;
  division: 'bronze' | 'silver' | 'gold' | 'platinum';
  cues: string[];
  falseCues: string[];
  invariant: string;
  bugs: string[];
}

const CONCEPTS: Concept[] = [
  {
    name: 'Prefix sums',
    division: 'silver',
    cues: ['Many range-sum queries', 'Array does not change between queries'],
    falseCues: ['Point updates between queries → use a Fenwick tree instead'],
    invariant: 'prefix[i] holds the sum of the first i elements; sum(l,r)=prefix[r]−prefix[l−1].',
    bugs: ['Off-by-one with 1-based vs 0-based prefix', 'Integer overflow on large sums'],
  },
  {
    name: 'Dijkstra',
    division: 'gold',
    cues: ['Non-negative edge weights', 'Single-source shortest path on a weighted graph'],
    falseCues: ['Negative weights → Bellman-Ford', 'Unit weights → plain BFS is simpler'],
    invariant:
      "Once a node is popped its distance is final — the frontier can't produce a shorter path.",
    bugs: ['Forgetting the stale-entry skip', 'int overflow on summed weights'],
  },
  {
    name: 'Two pointers',
    division: 'silver',
    cues: ['Monotonic window condition', 'Sorted input or non-negative values'],
    falseCues: ['“At most K” with negatives breaks the monotonic window'],
    invariant:
      'The window [l,r] always satisfies the constraint; advancing r only ever moves l right.',
    bugs: ['Moving the wrong pointer', 'Not shrinking when the condition breaks'],
  },
  {
    name: 'DSU / union-find',
    division: 'gold',
    cues: ['Connectivity / grouping queries', 'Merge sets, ask same-set'],
    falseCues: ['Need to split sets → DSU does not support deletion'],
    invariant:
      'find(x) returns the canonical representative of x’s set; union merges by rank/size.',
    bugs: ['Forgetting path compression', 'Union by the wrong representative'],
  },
];

export function Library() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Concept | null>(null);
  const list = CONCEPTS.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  if (open) {
    return (
      <div className="scr scr--narrow">
        <button className="lib-back" onClick={() => setOpen(null)}>
          ← Concept library
        </button>
        <div className="scr-head">
          <div>
            <div className="t-eyebrow">concept</div>
            <h1 className="scr-h">{open.name}</h1>
          </div>
          <Badge tone={open.division} dot>
            {open.division[0]!.toUpperCase() + open.division.slice(1)}
          </Badge>
        </div>
        <Panel title="Recognition cues" style={{ marginBottom: 12 }}>
          <ul className="lib-list">
            {open.cues.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </Panel>
        <Panel title="False cues" style={{ marginBottom: 12 }}>
          <ul className="lib-list lib-list--bad">
            {open.falseCues.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </Panel>
        <Panel title="Invariant" style={{ marginBottom: 12 }}>
          <p className="lib-invariant">{open.invariant}</p>
        </Panel>
        <Panel title="Common bugs">
          <ul className="lib-list lib-list--bad">
            {open.bugs.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Panel>
      </div>
    );
  }

  return (
    <div className="scr">
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">concept library</div>
          <h1 className="scr-h">Reference</h1>
        </div>
        <input
          className="uc-input"
          style={{ width: 240 }}
          placeholder="Search concepts…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="lib-grid">
        {list.map((c) => (
          <button key={c.name} className="lib-card" onClick={() => setOpen(c)}>
            <div className="lib-card__top">
              <span className="lib-card__nm">{c.name}</span>
              <Badge tone={c.division} dot>
                {c.division[0]!.toUpperCase() + c.division.slice(1)}
              </Badge>
            </div>
            <div className="lib-card__cue dim">{c.cues[0]}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
